"use client";

import Image from "next/image";
import { useState } from "react";
import type { BlockProps, StrapiBlock, } from "@/app/components/blocks/types";
import { ChevronLeft, ChevronRight } from "lucide-react";

type FonctionMembre =
  | "responsable_agence"
  | "dieteticien"
  | "livreur";

type Media = {
  id?: number;
  documentId?: string;
  url?: string | null;
  alternativeText?: string | null;
};

type Membre = {
  id?: number;
  documentId?: string;
  fonction?: FonctionMembre | null;
  image?: Media | null;
  description?: string | null;
};

type EquipeLocaleBlock = StrapiBlock & {
  __component: "blocks.equipe-locale";
  titre?: string | null;
  membre?: Membre[];
};

type DotsProps = {
  total: number;
  activeIndex: number;
  onChange: (index: number) => void;
};

function SliderDots({ total, activeIndex, onChange }: DotsProps) {
  if (total <= 1) return null;

  const showPrevious = () => {
    const previousIndex = activeIndex === 0 ? total - 1 : activeIndex - 1;
    onChange(previousIndex);
  };

  const showNext = () => {
    const nextIndex = activeIndex === total - 1 ? 0 : activeIndex + 1;
    onChange(nextIndex);
  };

  return (
    <div className="flex items-center justify-center gap-2">
      {/* Flèche précédente */}
      <button
        type="button"
        onClick={showPrevious}
        aria-label="Afficher le membre précédent"
        className="flex h-7 w-7 items-center justify-center rounded-full text-primaryGreen transition hover:bg-greenPastel"
      >
        <ChevronLeft size={20} strokeWidth={2.5} />
      </button>

      {/* Points */}
      <div className="flex items-center gap-1.5">
        {Array.from({ length: total }).map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => onChange(index)}
            aria-label={`Afficher le membre ${index + 1}`}
            aria-current={index === activeIndex ? "true" : undefined}
            className={`h-2.5 w-2.5 rounded-full border border-primaryGreen transition-colors ${index === activeIndex
              ? "bg-primaryGreen"
              : "bg-transparent hover:bg-greenPastel"
              }`}
          />
        ))}
      </div>

      {/* Flèche suivante */}
      <button
        type="button"
        onClick={showNext}
        aria-label="Afficher le membre suivant"
        className="flex h-7 w-7 items-center justify-center rounded-full text-primaryGreen transition hover:bg-greenPastel"
      >
        <ChevronRight size={20} strokeWidth={2.5} />
      </button>
    </div>
  );
}

type ResponsableCardProps = {
  membres: Membre[];
};

function ResponsableCard({
  membres,
}: ResponsableCardProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const membre = membres[activeIndex];


  if (!membre) return null;

  const imageUrl = membre.image?.url || "";
  const imageAlt = membre.image?.alternativeText || " ";

  return (
    <article className="flex h-full flex-col rounded-xl bg-gradient-to-b from-primaryforeground via-white to-white p-4 lg:p-7 shadow-[0_12px_30px_rgba(0,0,0,0.12)]">
      <div>
        <div className="relative h-[125px] w-[125px] lg:h-[145px] lg:w-[145px] rounded-full border-[4px] border-primaryGreen bg-white p-[7px]">
          <div className="relative h-full w-full overflow-hidden rounded-full">
            {imageUrl && (
              <Image
                src={imageUrl}
                alt={imageAlt}
                fill
                className="object-cover"
                sizes="145px"
              />
            )}
          </div>
        </div>
      </div>

      <h3 className="mt-4 text-2xl font-bold text-primaryGreen font-playfair">
        Responsable d’agence
      </h3>

      <div className="my-4 h-1 w-20 rounded-full bg-gradient-to-r from-primaryGreen to-transparent" />

      {membre.description && (
        <p className="text-[16px] lg:text-lg leading-relaxed text-textColor">
          {membre.description}
        </p>
      )}

      <div className="mt-auto pt-5">
        <SliderDots
          total={membres.length}
          activeIndex={activeIndex}
          onChange={setActiveIndex}
        />
      </div>
    </article>
  );
}

type GroupCardProps = {
  titre: string;
  membres: Membre[];
  variant?: "horizontal" | "vertical";
};

function GroupCard({
  titre,
  membres,
  variant = "horizontal",
}: GroupCardProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const membre = membres[activeIndex];

  if (!membre) return null;

  const imageUrl = membre.image?.url || "";
  const imageAlt =
    membre.image?.alternativeText || titre;

  // VERSION VERTICALE
  if (variant === "vertical") {
    return (
      <article className="flex h-full flex-col rounded-xl bg-white p-4 lg:p-7 shadow-[0_12px_30px_rgba(0,0,0,0.12)]">
        <div>
          <div className="relative h-[125px] w-[125px] lg:h-[145px] lg:w-[145px] rounded-full border-[3px] border-primaryGreen bg-white p-[6px]">
            <div className="relative h-full w-full overflow-hidden rounded-full">
              {imageUrl && (
                <Image
                  src={imageUrl}
                  alt={imageAlt}
                  fill
                  className="object-contain"
                  sizes="145px"
                />
              )}
            </div>
          </div>
        </div>

        <h3 className="mt-4 font-playfair text-2xl font-bold text-primaryGreen">
          {titre}
        </h3>

        <div className="my-4 h-1 w-20 rounded-full bg-gradient-to-r from-primaryGreen to-transparent" />

        {membre.description && (
          <p className="text-[16px] leading-relaxed text-textColor lg:text-lg">
            {membre.description}
          </p>
        )}

        <div className="mt-auto pt-5">
          <SliderDots
            total={membres.length}
            activeIndex={activeIndex}
            onChange={setActiveIndex}
          />
        </div>
      </article>
    );
  }

  // VERSION ACTUELLE
  return (
    <article className="grid gap-6 rounded-xl bg-white p-7 shadow-[0_12px_30px_rgba(0,0,0,0.12)] sm:grid-cols-[150px_1fr]">
      <div className="flex flex-col items-center">
        <div className="relative h-[120px] w-[120px] rounded-full border-[3px] border-primaryGreen bg-white p-[6px]">
          <div className="relative h-full w-full overflow-hidden rounded-full">
            {imageUrl && (
              <Image
                src={imageUrl}
                alt={imageAlt}
                fill
                className="object-contain"
                sizes="120px"
              />
            )}
          </div>
        </div>

        <div className="mt-2">
          <SliderDots
            total={membres.length}
            activeIndex={activeIndex}
            onChange={setActiveIndex}
          />
        </div>
      </div>

      <div>
        <h3 className="font-playfair text-2xl font-bold text-primaryGreen">
          {titre}
        </h3>

        <div className="my-3 h-1 w-20 rounded-full bg-gradient-to-r from-primaryGreen to-transparent" />

        {membre.description && (
          <p className="text-[16px] leading-relaxed text-textColor lg:text-lg">
            {membre.description}
          </p>
        )}
      </div>
    </article>
  );
}

export default function EquipeLocale({
  data,
}: BlockProps<EquipeLocaleBlock>) {
  const membres = Array.isArray(data.membre) ? data.membre : [];

  const responsables = membres.filter(
    (item) => item.fonction === "responsable_agence",
  );

  const dieteticiens = membres.filter(
    (item) => item.fonction === "dieteticien",
  );

  const livreurs = membres.filter(
    (item) => item.fonction === "livreur",
  );

  const hasResponsable = responsables.length > 0;
  const hasDieteticiens = dieteticiens.length > 0;
  const hasLivreurs = livreurs.length > 0;

  const secondaryGroupsCount =
    Number(hasDieteticiens) +
    Number(hasLivreurs);

  if (membres.length === 0) return null;

  return (
    <section className="bg-muted py-8 lg:py-20">
      <div className="lms-container">
        {data.titre && (
          <h2 className="mb-6 font-playfair text-3xl font-bold text-[#343434] lg:text-[44px]">
            {data.titre}
          </h2>
        )}

        {/* RESPONSABLE SEUL */}
        {hasResponsable && secondaryGroupsCount === 0 && (
          <div className="max-w-[1600px]">
            <ResponsableCard membres={responsables} />
          </div>
        )}

        {/* RESPONSABLE + UN SEUL GROUPE */}
        {hasResponsable && secondaryGroupsCount === 1 && (
          <div className="grid items-stretch gap-6 max-w-[1600px] lg:grid-cols-2">
            <ResponsableCard membres={responsables} />

            {hasDieteticiens && (
              <GroupCard
                titre="Nos diététiciens"
                membres={dieteticiens}
                variant="vertical"
              />
            )}

            {hasLivreurs && (
              <GroupCard
                titre="Une équipe de livreurs"
                membres={livreurs}
                variant="vertical"
              />
            )}
          </div>
        )}

        {/* RESPONSABLE + DIÉTÉTICIENS + LIVREURS */}
        {hasResponsable && secondaryGroupsCount === 2 && (
          <div className="grid items-stretch gap-6 max-w-[1600px] lg:grid-cols-[340px_1fr]">
            <ResponsableCard membres={responsables} />

            <div className="grid gap-6">
              <GroupCard
                titre="Nos diététiciens"
                membres={dieteticiens}
              />

              <GroupCard
                titre="Une équipe de livreurs"
                membres={livreurs}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}